import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { errore, fmtDataOra, Spiegabile, fmtTelefono, testoVeicolo } from "@/pages/crm/crmComuni";
import Recapiti from "@/pages/crm/Recapiti";
import { Phone } from "lucide-react";
import { TIPI_EVENTO, fmtEvento, perInput, MSG_LEAD_SENZA_TEL } from "./calendarioComuni";
import { ChiudiAttivita, Rimanda } from "./ChiusuraAttivita";
import { StoricoAttivita } from "./StoricoAttivita";
import { useGuardiaFinestra } from "./GuardiaFinestra";

// Sprint CAL-2 — nome e telefono: delle collegate dal contatto della lead (letto al momento), delle altre dai campi dell'attività
const manca = (v, lead, tipo) => tipo === "chiamata" && (lead ? !lead.telefoni?.length : !(v.contatto_telefono || "").trim());

function CampiContatto({ v, onCambia, lead, tipo, prefix }) {
  if (tipo === "todo") return null;
  if (lead) return (
    <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm" data-testid={`${prefix}-contatto-lead`}>
      Contatto della lead: <b>{lead.nome || "—"}</b>{lead.telefoni?.length ? ` · ${lead.telefoni.map(fmtTelefono).join(", ")}` : ""}
      {tipo === "chiamata" && !lead.telefoni?.length && <div className="text-rose-700" data-testid={`${prefix}-senza-telefono`}>{MSG_LEAD_SENZA_TEL}{" "}
        {lead.id && <Link to={`/crm/contatti/${lead.id}`} className="font-medium underline" data-testid={`${prefix}-apri-contatto`}>Apri il contatto</Link>}</div>}
    </div>);
  return (
    <div className="grid grid-cols-2 gap-2">
      <div><Label>Nome contatto</Label><Input value={v.contatto_nome || ""} onChange={(e) => onCambia({ contatto_nome: e.target.value })} data-testid={`${prefix}-contatto-nome`} /></div>
      <div><Label>Telefono{tipo === "chiamata" ? " *" : ""}</Label><Input type="tel" value={v.contatto_telefono || ""}
        onChange={(e) => onCambia({ contatto_telefono: e.target.value })} data-testid={`${prefix}-contatto-telefono`} /></div>
    </div>);
}

function ContattoAttivita({ ev }) {
  const c = ev.contatto_lead;
  const nome = c ? c.nome : ev.contatto_nome;
  const tel = c ? c.telefoni || [] : ev.contatto_telefono ? [ev.contatto_telefono] : [];
  if (ev.tipo === "todo" || (!nome && !tel.length)) return null;
  return (
    <div className="space-y-1 pb-1" data-testid="cal-scheda-contatto">
      {nome && <div className="font-medium text-slate-800" data-testid="cal-scheda-contatto-nome">{nome}</div>}
      {tel.map((t, i) => (
        <div key={t} className="flex items-center gap-2" data-testid={`cal-scheda-tel-${i}`}><Phone className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <span className="font-mono">{fmtTelefono(t)}</span>
          <Recapiti contatto={{ telefoni: [t] }} opportunita={[]} prefix={`cal-scheda-tel-${i}-az`} tipi={["chiamata", "whatsapp"]} /></div>))}
    </div>);
}


// Sprint CRM-5 — «Fine» come durata dall'inizio; «personalizzata» = fine scritta a mano
const DURATE = [["15", "15 min"], ["30", "30 min"], ["60", "1 ora"], ["90", "1 ora e mezza"], ["120", "2 ore"], ["180", "3 ore"],
  ["giorno", "Tutto il giorno"], ["altro", "Personalizzata"]];
const piu = (inizio, min) => {
  const d = new Date(inizio); if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() + min);
  const z = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};
export const fineDaDurata = (inizio, durata) => (/^\d+$/.test(durata) ? piu(inizio, Number(durata)) : "");
export const durataDi = (f) => {
  if (f.tutto_il_giorno) return "giorno";
  if (!f.fine) return "altro";
  const m = Math.round((new Date(f.fine) - new Date(f.inizio)) / 60000);
  return DURATE.some(([k]) => k === String(m)) ? String(m) : "altro";
};

// Inizio + durata (+ fine a mano con «personalizzata»); valore = { inizio, fine, tutto_il_giorno, durata }
function InizioFine({ v, onCambia, prefix }) {
  const cambia = (x) => {
    const n = { ...v, ...x };
    n.tutto_il_giorno = n.durata === "giorno";
    if (n.tutto_il_giorno) { n.inizio = `${(n.inizio || "").slice(0, 10)}T00:00`; n.fine = ""; }
    else if (n.durata !== "altro") n.fine = fineDaDurata(n.inizio, n.durata);
    onCambia(n);
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <div><Label>Inizio *</Label><Input type={v.tutto_il_giorno ? "date" : "datetime-local"} value={v.tutto_il_giorno ? (v.inizio || "").slice(0, 10) : v.inizio}
        onChange={(e) => cambia({ inizio: v.tutto_il_giorno ? `${e.target.value}T00:00` : e.target.value })} data-testid={`${prefix}-inizio`} /></div>
      <div><Label>Fine</Label>
        <Select value={v.durata} onValueChange={(d) => cambia({ durata: d })}>
          <SelectTrigger data-testid={`${prefix}-durata`}><SelectValue /></SelectTrigger>
          <SelectContent>{DURATE.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select></div>
      {v.durata === "altro" && <div className="col-span-2"><Label>Fine (a mano)</Label>
        <Input type="datetime-local" value={v.fine || ""} onChange={(e) => onCambia({ ...v, fine: e.target.value })} data-testid={`${prefix}-fine`} /></div>}
    </div>
  );
}

// scelte: [{ chiave, etichetta, calendario, company_id, site_id, proprietario_id }]
export function NuovoEvento({ scelte, iniziale, opportunita, onChiudi, onFatto }) {
  const [base] = useState(() => ({ chiave: scelte[0]?.chiave || "", tipo: "appuntamento", titolo: "", inizio: iniziale || "",
    fine: iniziale ? fineDaDurata(iniziale, "30") : "", tutto_il_giorno: false, descrizione: "", durata: "30", contatto_nome: "", contatto_telefono: "" }));
  const [f, setF] = useState(base);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const lead = opportunita ? opportunita.contatto || {} : null;
  const salva = async () => {
    const c = scelte.find((x) => x.chiave === f.chiave);
    try {
      await api.post("/calendario/eventi", { calendario: c.calendario, company_id: c.company_id, site_id: c.site_id,
        proprietario_id: c.proprietario_id, tipo: f.tipo, titolo: f.titolo, descrizione: f.descrizione, inizio: f.inizio,
        fine: f.fine || null, tutto_il_giorno: f.tutto_il_giorno, opportunita_id: opportunita?.id || null,
        ...(lead ? {} : { contatto_nome: f.contatto_nome, contatto_telefono: f.contatto_telefono }) });
      toast.success("Evento creato"); onFatto(); return true;
    } catch (e) { toast.error(errore(e)); return false; }
  };
  const { radice, chiudi, finestra } = useGuardiaFinestra({ sporco: JSON.stringify(f) !== JSON.stringify(base), salva, onChiudi });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) chiudi(); }}>
      <DialogContent ref={radice} className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cal-nuovo-dialog">
        <DialogHeader><DialogTitle>{opportunita ? "Nuova attività" : "Nuovo evento"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {opportunita && <div className="text-sm text-slate-600" data-testid="cal-nuovo-opportunita">Collegata a: <b>{opportunita.descrizione}</b></div>}
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Calendario</Label>
              <Select value={f.chiave} onValueChange={(v) => set("chiave", v)}>
                <SelectTrigger data-testid="cal-nuovo-calendario"><SelectValue /></SelectTrigger>
                <SelectContent>{scelte.map((c) => <SelectItem key={c.chiave} value={c.chiave}>{c.etichetta}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Tipo</Label>
              <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger data-testid="cal-nuovo-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TIPI_EVENTO).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Titolo *</Label><Input value={f.titolo} onChange={(e) => set("titolo", e.target.value)} data-testid="cal-nuovo-titolo" /></div>
          <CampiContatto v={f} onCambia={(n) => setF((x) => ({ ...x, ...n }))} lead={lead} tipo={f.tipo} prefix="cal-nuovo" />
          <InizioFine v={f} onCambia={(n) => setF((x) => ({ ...x, ...n }))} prefix="cal-nuovo" />
          <div><Label>Descrizione</Label><Textarea value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} data-testid="cal-nuovo-descrizione" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={chiudi} data-testid="cal-nuovo-annulla">Annulla</Button>
          <Button onClick={salva} disabled={!f.titolo.trim() || !f.inizio || !f.chiave || manca(f, lead, f.tipo)} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="cal-nuovo-salva">Salva</Button>
        </DialogFooter>
        {finestra}
      </DialogContent>
    </Dialog>
  );
}

export function SchedaEvento({ ev, onChiudi, onFatto }) {
  const confirm = useConfirm();
  const [chiudi, setChiudi] = useState(false);  // Sprint CAL-1 — finestra comune con la nota di chiusura obbligatoria
  const [mod, setMod] = useState(null);
  const [base, setBase] = useState(null);
  const azione = async (fn, msg) => { try { await fn(); toast.success(msg); onFatto(); return true; } catch (e) { toast.error(errore(e)); return false; } };
  const elimina = async () => {
    if (await confirm({ title: "Elimina evento", message: `Eliminare «${ev.titolo}»?`, confirmLabel: "SI, elimina" })) {
      azione(() => api.delete(`/calendario/eventi/${ev.id}`), "Evento eliminato");
    }
  };
  const [esitoMod, setEsitoMod] = useState(null);  // Sprint CRM-5 — esito correggibile anche a evento fatto
  const lead = ev.opportunita_id ? ev.contatto_lead || {} : null;
  const { durata, ...campi } = mod || {};
  const salva = () => azione(() => api.put(`/calendario/eventi/${ev.id}`, { ...campi, fine: campi.fine || null,
    originali: { titolo: ev.titolo, inizio: perInput(ev.inizio), fine: ev.fine ? perInput(ev.fine) : null, tutto_il_giorno: !!ev.tutto_il_giorno,
      descrizione: ev.descrizione } }), "Evento modificato");
  const apriModifica = () => {
    const base = { titolo: ev.titolo, contatto_nome: ev.contatto_nome || "", contatto_telefono: ev.contatto_telefono || "",
      inizio: perInput(ev.inizio), fine: ev.fine ? perInput(ev.fine) : "", tutto_il_giorno: !!ev.tutto_il_giorno, descrizione: ev.descrizione };
    setMod({ ...base, durata: durataDi(base) });
    setBase({ ...base, durata: durataDi(base) });
  };
  const { radice, chiudi: esci, finestra } = useGuardiaFinestra({ sporco: !!mod && JSON.stringify(mod) !== JSON.stringify(base), salva,
    scarta: () => setMod(null), onChiudi });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) esci(); }}>
      <DialogContent ref={radice} className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cal-scheda-dialog">
        <DialogHeader><DialogTitle data-testid="cal-scheda-titolo">{ev.titolo}</DialogTitle></DialogHeader>
        {mod ? (
          <div className="space-y-2">
            <Input value={mod.titolo} onChange={(e) => setMod({ ...mod, titolo: e.target.value })} data-testid="cal-mod-titolo" />
            <CampiContatto v={mod} onCambia={(n) => setMod({ ...mod, ...n })} lead={lead} tipo={ev.tipo} prefix="cal-mod" />
            <InizioFine v={mod} onCambia={setMod} prefix="cal-mod" />
            <Textarea value={mod.descrizione || ""} onChange={(e) => setMod({ ...mod, descrizione: e.target.value })} data-testid="cal-mod-descrizione" />
          </div>
        ) : (
          <div className="space-y-1 text-sm" data-testid="cal-scheda-dati">
            <ContattoAttivita ev={ev} />
            {ev.opportunita_id && <div className="flex flex-wrap items-center gap-x-3" data-testid="cal-scheda-lead">
              <Link className="text-blue-600 font-medium hover:underline" to={`/crm/opportunita/${ev.opportunita_id}`}
                data-testid="cal-scheda-opportunita">Opportunità: {ev.opportunita_descrizione || "apri"}</Link>
              <span className="text-slate-600" data-testid="cal-scheda-veicolo">{testoVeicolo(ev.opportunita_veicolo)}</span></div>}
            <div>{TIPI_EVENTO[ev.tipo]} · {fmtEvento(ev)} · {ev.calendario === "sede" ? `Sede ${ev.sede_nome || "—"}` : ev.proprietario_nome}</div>
            {ev.assegnato_da_nome && <div className="text-slate-500">Assegnato da {ev.assegnato_da_nome}</div>}
            {ev.descrizione && <div className="whitespace-pre-wrap rounded-md border border-slate-300 bg-slate-50 px-3 py-2 my-1" data-testid="cal-scheda-descrizione">{ev.descrizione}</div>}
            {ev.fatto ? <div className="space-y-2">
              <div className="text-emerald-700" data-testid="cal-scheda-fatto">Fatto{ev.esito ? ` · ${ev.esito}` : ""}</div>
              {/* Sprint CAL-1 — nota di chiusura e chi/quando l'ha chiusa restano visibili */}
              {(ev.fatto_da_nome || ev.fatto_il) && <div className="text-xs text-slate-500" data-testid="cal-scheda-chiusa-da">
                Chiusa{ev.fatto_da_nome ? ` da ${ev.fatto_da_nome}` : ""}{ev.fatto_il ? ` il ${fmtDataOra(ev.fatto_il)}` : ""}</div>}
              {ev.puo_modificare && (esitoMod === null
                ? <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => azione(() => api.post(`/calendario/eventi/${ev.id}/riapri`), "Evento riaperto")}
                    data-testid="cal-scheda-riapri">Riapri</Button>
                  <Button size="sm" variant="outline" onClick={() => setEsitoMod(ev.esito || "")} data-testid="cal-scheda-modifica-esito">Modifica esito</Button></div>
                : <div className="flex gap-2"><Input value={esitoMod} onChange={(e) => setEsitoMod(e.target.value)} data-testid="cal-scheda-esito-nuovo" />
                  <Button size="sm" onClick={() => azione(() => api.post(`/calendario/eventi/${ev.id}/fatto`, { esito: esitoMod }), "Esito modificato")}
                    data-testid="cal-scheda-esito-salva">Salva esito</Button></div>)}
            </div>
              : ev.puo_modificare && <div className="flex gap-2 pt-2">
                <Button onClick={() => setChiudi(true)} data-testid="cal-scheda-segna-fatto">Segna fatto</Button>
                <Rimanda ev={ev} onFatto={onFatto} testid="cal-scheda-rimanda" /></div>}
          </div>
        )}
        <StoricoAttivita id={ev.id} />
        {chiudi && <ChiudiAttivita ev={ev} onChiudi={() => setChiudi(false)} onFatto={() => { setChiudi(false); onFatto(); }} />}
        {/* Sprint CRM-2C — senza permesso di scrittura i comandi non compaiono (il server risponde 403) */}
        {ev.puo_modificare && <DialogFooter>
          <Spiegabile motivo={ev.puo_eliminare === false ? "Serve «Calendario: elimina To do List»" : null} testid="cal-scheda-elimina-spiega">
            <Button variant="outline" onClick={elimina} disabled={ev.puo_eliminare === false} className="text-rose-700" data-testid="cal-scheda-elimina">Elimina</Button></Spiegabile>
          {mod ? <Button onClick={salva} disabled={manca(mod, lead, ev.tipo)} data-testid="cal-mod-salva">Salva</Button>
            : <Button variant="outline" onClick={apriModifica} data-testid="cal-scheda-modifica">Modifica</Button>}
        </DialogFooter>}
        {finestra}
      </DialogContent>
    </Dialog>
  );
}
